import { isValidAadhaarLast4, containsFullAadhaar } from '@/core';
import type { SavedPlayer, SavedTeam } from './types';

/** Normalised name for duplicate checks: trimmed, single spaces, case-insensitive. */
function key(name: string): string {
  return name.trim().replace(/\s+/g, ' ').toLocaleLowerCase();
}

/** True when two team names are the same, ignoring case and extra spaces. */
export function sameTeamName(a: string, b: string): boolean {
  return key(a) === key(b);
}

/** The saved team with the same name (ignoring case and extra spaces), if any. */
export function findTeamByName(teams: readonly SavedTeam[], name: string): SavedTeam | undefined {
  const k = key(name);
  return k === '' ? undefined : teams.find((t) => key(t.name) === k);
}

/** UUID where available (all current browsers, Android WebView); a random id otherwise. */
export function newTeamId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** The last line of defence for §5.6: a full Aadhaar number must never reach storage. */
export function assertStorablePlayer(player: SavedPlayer): void {
  const badLast4 = player.aadhaarLast4 !== undefined && !isValidAadhaarLast4(player.aadhaarLast4);
  const elsewhere = `${player.name}\n${player.registrationId ?? ''}`.split('\n');
  if (badLast4 || elsewhere.some(containsFullAadhaar)) {
    throw new Error('Only the last 4 Aadhaar digits can be saved');
  }
}

/** Sorts jersey numbers as people read them: 0, 00, 1, 2 … 10, 11. */
export function byJerseyNumber(a: SavedPlayer, b: SavedPlayer): number {
  return Number(a.number) - Number(b.number) || a.number.length - b.number.length;
}
